/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Fragment, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { visuallyHidden } from "@mui/utils";

import { IDatagridSimple, TDatagridData } from "../types";
import { CreateChip } from "../components/chip";
import { MultipleCol } from "../components/multiple-col";
import { IconDg } from "../components/icon";
import { ColorCel } from "../components/color";
import { SeeMore } from "../components/see-more";
import { ModalGeneric } from "../../modal-template/modal-generic";
import { useNavigationContext } from "../../../contexts";
import { RulesContainer, RulesText } from "./styles";
import theme from "../../../styles/theme";
import { ModalDelete } from "@/components/modal-template/modal-delete";
import { ContentConditional } from "@/components/content-conditional";
import {
  formatDateTime,
  formatText,
  getComparator,
  renderDelete,
  renderDownload,
  renderEdit
} from "../utils/datagrid";

// NOVO: Interface estendida para adicionar a nova prop opcionalmente.
// Isso garante que o uso antigo do componente não quebre.
 

/* ===== Componente Principal ===== */
export const DefaultDatagrid: React.FC<IDatagridSimple> = ({
  navigateTable,
  handleDelete,
  handleDownload,
  handleEdit,
  checkedRows,
  headers,
  data,
  count,
  hasCheckbox = false,
  loading = false,
  orderBy: orderByParam,
  page,
  renderExpandedRow // Prop para a funcionalidade de expandir
}) => {
  /* ===== Estados e Contextos ===== */
  const {
    seeMoreText,
    seeMoreModalWidth,
    seeMoreTitle,
    rowsPerPage,
    setRowsPerPage,
    order,
    setOrder
  } = useNavigationContext();
  const [selected, setSelected] = useState<number[]>([]);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [seeMoreVisible, setSeeMoreVisible] = useState<boolean>(false);
  const [orderBy, setOrderBy] = useState<string>(orderByParam ?? "");
  const [modalDelete, setModalDelete] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [expandedRows, setExpandedRows] = useState<number[]>([]); // Estado para as linhas expandidas

  /* ===== Funções Auxiliares ===== */
  const toogleModalSeeMore = () => setSeeMoreVisible((prevState) => !prevState);
  const toggleModalDelete = () => setModalDelete((prevState) => !prevState);

  const handleRowExpandToggle = (rowId: number) => {
    setExpandedRows(
      expandedRows.includes(rowId)
        ? expandedRows.filter((id) => id !== rowId)
        : [...expandedRows, rowId]
    );
  };

  /* ===== Paginação e Ordenação ===== */
  const visibleRows = useMemo(() => {
    return [...data].sort(getComparator(order, orderBy)).slice(0, rowsPerPage);
  }, [page, data, rowsPerPage, order, orderBy]);

  /* ===== Renderizadores de Células (Funções originais) ===== */
  const renderCell = (label: string, row: TDatagridData) => {
    switch (label) {
      case "chip":
      case "chip1":
        return (
          <TableCell key={`${row.id}-${label}`}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                columnGap: "0.5vw"
              }}
            >
              {CreateChip(row[label])}
            </div>
          </TableCell>
        );
      case "multipleCol":
      case "multipleCol1":
        return MultipleCol(row[label]);
      case "icon":
      case "icon1":
        return IconDg(row[label]);
      case "color":
      case "color1":
        return ColorCel(row[label]);
      case "seeMore":
      case "seeMore1":
        return SeeMore({ text: row[label], toggle: toogleModalSeeMore });
      default:
        if (label !== "id") {
          let value = row[label];
          if (typeof value === "string") {
            value = formatDateTime(value);
          }
          return (
            <TableCell
              key={`${row.id}-${label}`}
              sx={{
                padding: "10px",
                color: "black",
                fontFamily: "sans-serif",
                fontWeight: "100"
              }}
            >
              {value}
            </TableCell>
          );
        }
        return null;
    }
  };

  const renderRowCells = (row: TDatagridData) => {
    return Object.keys(row).map((r) => renderCell(r, row));
  };

  const hasTools = (row: any) => {
    const hasActionsColumn = headers.some((h) => h.field === "Ações");
    if (!hasActionsColumn) return null;

    const hasActionHandlers = handleDownload || handleEdit || handleDelete;
    if (hasActionHandlers) {
      return (
        <TableCell
          key={`tools-${row.id}`}
          style={{
            textAlign: "center",
            display: "flex",
            borderBottom: "none",
            padding: "10px"
          }}
        >
          {handleDownload && renderDownload(row, handleDownload)}
          {handleEdit && renderEdit(row, handleEdit)}
          {handleDelete &&
            renderDelete(row, setItemToDelete, toggleModalDelete)}
        </TableCell>
      );
    }
    return <TableCell key={`tools-empty-${row.id}`} />;
  };

  /* ===== Funções de Checkbox (Originais) ===== */
  const addOnlyUnique = (prev: number[], newIds: number[]) =>
    newIds.filter((n) => !prev.includes(n));
  const removeItemsFromArray = (array: number[], itemsToRemove: number[]) =>
    array.filter((item) => !itemsToRemove.includes(item));

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setIsChecked(true);
      const newSelecteds = visibleRows.map((row) => row.id);
      const updatedSelected = selected.length
        ? [...selected, ...addOnlyUnique(selected, newSelecteds)]
        : newSelecteds;
      setSelected(updatedSelected);
      checkedRows && checkedRows(updatedSelected);
    } else {
      setIsChecked(false);
      const unSelecteds = visibleRows.map((row) => row.id);
      const updatedSelected = removeItemsFromArray(selected, unSelecteds);
      setSelected(updatedSelected);
      checkedRows && checkedRows(updatedSelected);
    }
  };

  const isSelected = (id: number) => selected.includes(id);

  const handleCheckboxClick = (id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];
    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = [
        ...selected.slice(0, selectedIndex),
        ...selected.slice(selectedIndex + 1)
      ];
    }
    setSelected(newSelected);
    checkedRows && checkedRows(newSelected);
  };

  /* ===== Componente Cabeçalho da Tabela ===== */
  const ProTableHead = () => {
    const createSortHandler = (property: string) => () => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    };

    return (
      <TableHead
        sx={{
          position: "sticky",
          top: 0,
          backgroundColor: theme.colors.palete.brownExtraLighter,
          zIndex: 2
        }}
      >
        <TableRow sx={{ borderBottom: "1px solid rgba(224, 224, 224, 1)" }}>
          {/* Célula do cabeçalho para expandir (com largura fixa) */}
          {renderExpandedRow && (
            <TableCell padding="none" sx={{ width: "15px" }} />
          )}
          {hasCheckbox && (
            <TableCell padding="checkbox">
              <Checkbox
                checked={isChecked}
                onChange={handleSelectAllClick}
                inputProps={{ "aria-label": "selecionar todos" }}
              />
            </TableCell>
          )}
          {headers.map((item, index) => (
            <TableCell
              key={`header-${index}`}
              sx={{
                width: item?.width ?? "inherit",
                color: "black",
                fontFamily: "sans-serif",
                fontWeight: "800"
              }}
            >
              {item.isSort ? (
                <TableSortLabel
                  active={orderBy === item.field}
                  direction={orderBy === item.field ? order : "asc"}
                  onClick={createSortHandler(item.field)}
                >
                  {item.field}
                  {orderBy === item.field && (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  )}
                </TableSortLabel>
              ) : (
                item.field
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  /* ===== Componente para cada Linha (Principal + Detalhes) ===== */
  const Row = ({ row, index }: { row: TDatagridData; index: number }) => {
    const isRowExpanded = expandedRows.includes(row.id);
    const colSpan =
      headers.length + (hasCheckbox ? 1 : 0) + (renderExpandedRow ? 1 : 0);

    return (
      <Fragment>
        {/* Linha principal com os dados */}
        <TableRow
          key={row.id}
          sx={{
            "& > *": { borderBottom: "unset" },
            backgroundColor:
              index % 2 !== 0
                ? theme.colors.palete.tableOdd
                : theme.colors.palete.brownExtraLighter
          }}
        >
          {/* Célula com o botão de expandir (com largura fixa) */}
          {renderExpandedRow && (
            <TableCell
              padding="none"
              sx={{ width: "15px", textAlign: "center" }}
            >
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowExpandToggle(row.id);
                }}
              >
                {isRowExpanded ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </TableCell>
          )}
          {hasCheckbox && (
            <TableCell
              padding="checkbox"
              onClick={() => handleCheckboxClick(row.id)}
            >
              <Checkbox
                checked={isSelected(row.id)}
                inputProps={{ "aria-labelledby": `checkbox-${row.id}` }}
              />
            </TableCell>
          )}
          {renderRowCells(row)}
          {hasTools(row)}
        </TableRow>
        {/* Linha de detalhes (colapsável) */}
        {renderExpandedRow && (
          <TableRow>
            <TableCell style={{ padding: 0 }} colSpan={colSpan}>
              <Collapse in={isRowExpanded} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    margin: 1.5,
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                    padding: 2,
                    borderRadius: 1
                  }}
                >
                  {renderExpandedRow(row)}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  };

  /* ===== Retorno JSX Principal ===== */
  return (
    <>
      <ModalGeneric
        isOpen={seeMoreVisible}
        toggle={toogleModalSeeMore}
        title={seeMoreTitle}
        maxWidth={seeMoreModalWidth}
      >
        <RulesContainer>
          <RulesText>{formatText(seeMoreText)}</RulesText>
        </RulesContainer>
      </ModalGeneric>
      <ContentConditional.IsShow isShow={!!handleDelete}>
        <ModalDelete
          isOpen={modalDelete}
          title={handleDelete?.modalTitle ?? ""}
          action={async () => {
            handleDelete!.onDelete(itemToDelete);
            setItemToDelete(null);
            toggleModalDelete();
          }}
          description={handleDelete?.modalText ?? ""}
          toggle={toggleModalDelete}
          isDisabledButton={false}
        />
      </ContentConditional.IsShow>

      <TableContainer sx={{ maxHeight: "700px", overflowY: "auto" }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
            sx={{ backgroundColor: theme.colors.palete.brownExtraLighter }}
          >
            <CircularProgress color="inherit" />
          </Box>
        ) : visibleRows && visibleRows.length > 0 ? (
          <Table
            stickyHeader
            sx={{ backgroundColor: theme.colors.palete.tableOdd }}
          >
            <ProTableHead />
            <TableBody>
              {visibleRows.map((row, index) => (
                <Row key={row.id} row={row} index={index} />
              ))}
            </TableBody>
            <TableFooter
              sx={{
                position: "sticky",
                bottom: 0,
                backgroundColor: theme.colors.palete.brownExtraLighter,
                zIndex: 2
              }}
            >
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 50, 100]}
                  labelRowsPerPage="Linhas por página"
                  count={count}
                  rowsPerPage={rowsPerPage}
                  page={page - 1}
                  onPageChange={(_, newPage) => {
                    navigateTable((newPage + 1).toString());
                  }}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value));
                  }}
                  labelDisplayedRows={({ from, to, count }) =>
                    `Mostrando ${from} até ${to} de ${
                      count !== -1 ? count : `mais de ${to}`
                    }`
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
            sx={{ backgroundColor: theme.colors.palete.brownExtraLighter }}
          >
            <Typography
              variant="h6"
              color="textSecondary"
              sx={{ fontFamily: "sans-serif", fontWeight: "400" }}
            >
              Nenhum registro encontrado.
            </Typography>
          </Box>
        )}
      </TableContainer>
    </>
  );
};
